import { useState } from 'react';
import { PlayIcon } from '@heroicons/react/24/outline';
import { useHeldRobotTasks, useOverrideRobotHold } from '../../hooks/useRobotHolds';
import { HeldByText } from '../common/HeldByText';
import type { HeldRobotTask } from '../../services/api/robotHolds';

/**
 * MKS-241 (D-06) — held Robot tasks of one project, with the admin-only
 * "Dispatch anyway" override. Mounted by RoutingOverridePanel; the page itself
 * is `AdminRoute` and the server enforces `requireAdmin`.
 */

function waitingText(row: HeldRobotTask): string {
  if (row.waitingFor === 'triager') return 'Next triager pick';
  return row.heldBy.length === 0
    ? 'Clear; goes to the Robot within 5 minutes'
    : 'Approved; goes to the Robot when clear';
}

export function HeldRobotTasksSection({ projectId }: { projectId: number }) {
  const { data, isLoading, error } = useHeldRobotTasks(projectId);
  const overrideMutation = useOverrideRobotHold(projectId);
  const [overridingTaskId, setOverridingTaskId] = useState<number | null>(null);

  const rows = data ?? [];

  const handleOverride = (row: HeldRobotTask) => {
    setOverridingTaskId(row.taskId);
    overrideMutation.mutate(row.taskId, { onSettled: () => setOverridingTaskId(null) });
  };

  return (
    <div className="mt-4">
      <p className="text-sm font-semibold text-gray-900">Held tasks</p>
      <p className="text-sm text-gray-500 mt-0.5">
        A held task waits for unfinished Robot work in the same area, or for a task that blocks it,
        and goes out by itself once that is done.
      </p>

      {overrideMutation.isError && (
        <p className="mt-2 text-sm text-red-600">{(overrideMutation.error as Error).message}</p>
      )}

      {isLoading ? (
        <p className="mt-3 text-sm text-gray-400">Loading held tasks…</p>
      ) : error ? (
        <p className="mt-3 text-sm text-red-600">Failed to load held tasks.</p>
      ) : rows.length === 0 ? (
        <p className="mt-3 text-sm text-gray-500">No held tasks on this project.</p>
      ) : (
        <div className="mt-3 overflow-auto rounded-md border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase">
                  Task
                </th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase">
                  Reason
                </th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase">
                  Waiting
                </th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {rows.map((row, idx) => (
                <tr key={row.taskId} className={idx % 2 === 1 ? 'bg-gray-50' : 'bg-white'}>
                  <td className="px-4 py-2 text-sm whitespace-nowrap">
                    {row.code ? <span className="font-medium text-gray-900">{row.code}</span> : null}
                    <span className="text-gray-700"> {row.name}</span>
                  </td>
                  <td className="px-4 py-2 text-sm text-gray-700">
                    <HeldByText heldBy={row.heldBy} />
                  </td>
                  <td className="px-4 py-2 text-sm text-gray-700 whitespace-nowrap">
                    {waitingText(row)}
                  </td>
                  <td className="px-4 py-2 text-right whitespace-nowrap">
                    <button
                      type="button"
                      onClick={() => handleOverride(row)}
                      disabled={overridingTaskId === row.taskId}
                      aria-label={`Dispatch ${row.code ?? row.name} anyway`}
                      className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-blue-600 disabled:opacity-40 disabled:hover:text-gray-500"
                    >
                      <PlayIcon className="h-4 w-4" />
                      Dispatch anyway
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
